import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, UserPlus, Ban, RotateCcw } from "lucide-react";
import { PayBadge } from "@/components/PaymentHistory";

const SOURCE = { purchase: "Purchased", free: "Free", complimentary: "Complimentary (admin)", complimentary_legacy: "Complimentary (legacy)", self: "Purchased" };

export default function EnrollmentsTab() {
  const [enrs, setEnrs] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [expires, setExpires] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => Promise.all([
    api.get("/admin/enrollments"),
    api.get("/admin/users"),
    api.get("/admin/courses"),
  ]).then(([e, u, c]) => { setEnrs(e.data); setUsers(u.data); setCourses(c.data); });
  useEffect(() => { load(); }, []);

  const enroll = async () => {
    try {
      await api.post("/admin/enrollments", {
        user_id: userId, course_id: courseId,
        expires_at: expires || null,
      });
      toast.success("Enrolled");
      setOpen(false); setUserId(""); setCourseId(""); setExpires("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const remove = async (id) => {
    if (!confirm("Delete this enrollment record permanently? (Use Revoke to keep history.)")) return;
    await api.delete(`/admin/enrollments/${id}`);
    toast.success("Removed"); load();
  };
  const setStatus = async (id, status) => {
    await api.put(`/admin/enrollments/${id}`, { status });
    toast.success(status === "revoked" ? "Access revoked" : "Access restored"); load();
  };
  const shown = filter === "all" ? enrs : enrs.filter((e) => e.course_id === filter);
  const updateRole = async (uid, role) => {
    await api.put(`/admin/users/${uid}/role`, { role });
    toast.success("Role updated"); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-72" data-testid="enroll-course-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All courses ({enrs.length})</SelectItem>
            {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({enrs.filter((e) => e.course_id === c.id).length})</SelectItem>)}
          </SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="new-enrollment-btn">
              <UserPlus className="mr-2 h-4 w-4" /> Enroll student
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Grant complimentary access</DialogTitle></DialogHeader>
            <div className="grid gap-4">
              <div>
                <Label>Student</Label>
                <Select value={userId} onValueChange={setUserId}>
                  <SelectTrigger data-testid="enroll-user"><SelectValue placeholder="Pick student" /></SelectTrigger>
                  <SelectContent>
                    {users.filter((u) => u.role === "student").map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Course</Label>
                <Select value={courseId} onValueChange={setCourseId}>
                  <SelectTrigger data-testid="enroll-course"><SelectValue placeholder="Pick course" /></SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Access expires on (optional)</Label>
                <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="enroll-expires" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={enroll} disabled={!userId || !courseId} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="enroll-save-btn">Enroll</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border/70">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Course</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Enrolled</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shown.map((e) => (
                <TableRow key={e.id} data-testid={`enrollment-row-${e.id}`}>
                  <TableCell>
                    <div className="font-medium">{e.user?.name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{e.user?.email}</div>
                  </TableCell>
                  <TableCell>{e.course?.name || "—"}</TableCell>
                  <TableCell><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${e.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`}>{e.status}</span></TableCell>
                  <TableCell className="text-xs">{SOURCE[e.source] || "Admin"}</TableCell>
                  <TableCell className="text-xs">{e.payment ? <span className="flex items-center gap-1"><PayBadge status={e.payment.status} /> ₹{e.payment.amount}</span> : "—"}</TableCell>
                  <TableCell className="text-xs">{e.expires_at ? new Date(e.expires_at).toLocaleDateString() : "No expiry"}</TableCell>
                  <TableCell className="text-xs">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {e.status === "active" ? (
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setStatus(e.id, "revoked")} data-testid={`revoke-enroll-${e.id}`}><Ban className="mr-1 h-3.5 w-3.5" /> Revoke</Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => setStatus(e.id, "active")} data-testid={`restore-enroll-${e.id}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore</Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => remove(e.id)} data-testid={`del-enroll-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-border/70">
        <CardContent className="p-6">
          <div className="font-display text-base font-bold">User roles</div>
          <p className="mt-1 text-sm text-muted-foreground">Promote students to teachers so you can assign them as class instructors.</p>
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Current role</TableHead><TableHead>Change</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{u.role}</span></TableCell>
                    <TableCell>
                      <Select defaultValue={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                        <SelectTrigger className="w-36" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="teacher">Teacher</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
